import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Button from '../../../../../ui/canon/Button';
import { 
  DownloadIcon, 
  EditIcon,
  FileTextIcon,
  ZoomInIcon,
  ZoomOutIcon,
  PartyPopperIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BarChart3Icon,
  CalendarIcon,
  MailIcon,
  Share2Icon,
  UserIcon,
  TrendingUpIcon,
  ArchiveIcon,
  CheckCircleIcon,
  Loader2Icon,
  SparklesIcon,
  LayoutListIcon,
  BookOpenIcon,
  FileIcon
} from 'lucide-react';
import { 
  DocumentGeneratorService, 
  generatePlanDocumentByType,
  planTypeHasAssessment 
} from '../services/DocumentGeneratorService';
import { PDFExportService } from '../services/PDFExportService';
import SuccessionReadinessAssessment from './SuccessionReadinessAssessment';
import { CeAssessmentContainer, CeAssessmentIntroCard } from '../CeAssessment';
import { GraduationCapIcon } from 'lucide-react';
import PlanPdfCurrentView from '../components/PlanPdfCurrentView';
import GuidedTour from '../../Shared/GuidedTour/GuidedTour';

const GET_PLAN_RESULT = gql`
  query GetPlanResult($planTemplateId: ID!) {
    userPlanProgress(planTemplateId: $planTemplateId) {
      id
      status
      completionPercentage
      planTemplate {
        id
        name
        description
        planType
        planModules {
          id
          title
          planSections {
            id
            title
            planQuestions {
              id
              questionText
              widgetType
              options
              purpose
              category
              outputSection
              gapAction
              metadata
            }
          }
        }
      }
      userPlanResponses {
        id
        answer
        planQuestionId
      }
    }
    getCurrentUser {
      id
      name
      allowed_name
      vmi_readiness_score
      diag_owner_answers
      diag_business_answers
      features
      company {
        id
        name
        features
      }
    }
  }
`;

const SAVE_PLAN_TO_VAULT = gql`
  mutation SavePlanToVault($planProgressId: ID!, $documentType: String!, $content: String!, $filename: String) {
    save_plan_to_vault(
      plan_progress_id: $planProgressId
      document_type: $documentType
      content: $content
      filename: $filename
    ) {
      id
      title
      category
      source_type
    }
  }
`;

const PlanCompletionCelebration = ({ planTemplateId, onEditPlan, onEmail, onShare }) => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(GET_PLAN_RESULT, {
    variables: { planTemplateId }
  });
  
  const [savePlanToVault] = useMutation(SAVE_PLAN_TO_VAULT);
  
  // Tab state: 'plan' or 'assessment'
  const [activeTab, setActiveTab] = useState('plan');
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showVaultMenu, setShowVaultMenu] = useState(false);
  const [vaultSaveStatus, setVaultSaveStatus] = useState({}); // { plan: 'idle'|'saving'|'saved', assessment: 'idle'|'saving'|'saved' }
  const [showCompletionTour, setShowCompletionTour] = useState(false);
  const tourTriggeredRef = useRef(false);
  const documentRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const vaultMenuRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
      if (vaultMenuRef.current && !vaultMenuRef.current.contains(event.target)) {
        setShowVaultMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle URL query parameters for actions and tabs
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = new URL(window.location.href);
    let shouldUpdateUrl = false;
    const action = params.get('action');
    
    // Auto-open download menu if URL has ?action=export
    if (action === 'export') {
      setShowDownloadMenu(true);
      url.searchParams.delete('action');
      shouldUpdateUrl = true;
    }
    
    // Auto-switch to CE tab if URL has ?tab=ce-assessment
    if (params.get('tab') === 'ce-assessment') {
      setActiveTab('ce-assessment');
      url.searchParams.delete('tab');
      shouldUpdateUrl = true;
    }
    
    // Auto-export PDF if URL has ?action=export_pdf
    // Note: export needs the DOM to be rendered, so we do a short retry loop.
    if (action === 'export_pdf') {
      url.searchParams.delete('action');
      shouldUpdateUrl = true;
      const maxAttempts = 30;
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        if (documentRef.current) {
          try {
            await handleDownload('pdf');
          } catch (e) {
            // keep best-effort; user can still export manually
            // eslint-disable-next-line no-console
            console.error('Auto export PDF failed:', e);
          }
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(tick, 100);
        }
      };
      setTimeout(tick, 0);
    }

    // Clean up URL without reloading page
    if (shouldUpdateUrl) {
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // When switching tabs, scroll back to the top of the tab/page content.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const opts = { top: 0, left: 0, behavior: 'smooth' };

    if (container && typeof container.scrollTo === 'function') {
      container.scrollTo(opts);
    } else if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo(opts);
    }
  }, [activeTab]);

  // Trigger completion tour after confetti animation (~3.5s delay)
  useEffect(() => {
    if (loading || !data) return;
    if (tourTriggeredRef.current) return;
    
    const progressId = data?.userPlanProgress?.id;
    const tourKey = `plan_completion_tour_shown_${progressId || planTemplateId}`;
    
    try {
      const params = new URLSearchParams(window.location.search);
      const tourParam = params.get('tour');
      
      // Handle URL parameter overrides for testing
      if (tourParam === 'reset') {
        localStorage.removeItem(tourKey);
        tourTriggeredRef.current = true;
        setTimeout(() => setShowCompletionTour(true), 500);
        return;
      }
      
      if (tourParam === 'open') {
        tourTriggeredRef.current = true;
        setTimeout(() => setShowCompletionTour(true), 500);
        try { localStorage.setItem(tourKey, 'true'); } catch (_) {}
        return;
      }
      
      const hasSeenTour = localStorage.getItem(tourKey) === 'true';
      if (hasSeenTour) return;
      
      // Mark as triggered and set timer
      tourTriggeredRef.current = true;
      
      // Delay to allow confetti animation to complete (~3.5s)
      const timer = setTimeout(() => {
        setShowCompletionTour(true);
      }, 3500);
      
      return () => clearTimeout(timer);
    } catch (_) {
      // localStorage may be unavailable
    }
  }, [loading, data, planTemplateId]);

  // Auto-download when ?autoDownload=pdf is in the URL (e.g. triggered from PactBuilder)
  useEffect(() => {
    if (loading || !data || !documentRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('autoDownload') === 'pdf') {
      // Remove param so back-navigation doesn't re-trigger
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      handleDownload('pdf');
    }
  }, [loading, data]);

  // Test helper for QA - allows resetting the tour
  useEffect(() => {
    const progressId = data?.userPlanProgress?.id;
    const tourKey = `plan_completion_tour_shown_${progressId || planTemplateId}`;
    
    window.resetPlanCompletionTour = (openImmediately = true) => {
      try { localStorage.removeItem(tourKey); } catch (_) {}
      tourTriggeredRef.current = false;
      if (openImmediately) {
        tourTriggeredRef.current = true;
        setShowCompletionTour(true);
      }
    };
    
    return () => {
      delete window.resetPlanCompletionTour;
    };
  }, [data, planTemplateId]);

  // Build dynamic tour steps based on feature flags
  // Must be before early returns to maintain hook order
  const userFeatures = data?.getCurrentUser?.features || [];
  const companyFeatures = data?.getCurrentUser?.company?.features || [];
  const planType = data?.userPlanProgress?.planTemplate?.planType;
  const valueAccelerationEnabledForTour = userFeatures.includes('value_acceleration') || companyFeatures.includes('value_acceleration');
  const ceAssessmentEnabledForTour = companyFeatures.includes('plan_builder_ce');
  const isAssessmentPlanForTour = valueAccelerationEnabledForTour && (planType === 'succession' || planType === null || planType === undefined);
  
  const completionTourSteps = useMemo(() => {
    const steps = [
      {
        id: 'plan-tab',
        title: intl.formatMessage({ id: 'succession.plans.tour_plan_ready_title' }),
        description: intl.formatMessage({ id: 'succession.plans.tour_plan_ready_description' }),
        targetSelector: '[data-tour="plan-tab"]',
        position: 'bottom'
      }
    ];

    if (isAssessmentPlanForTour) {
      steps.push({
        id: 'assessment-tab',
        title: intl.formatMessage({ id: 'succession.plans.tour_assessment_title' }),
        description: intl.formatMessage({ id: 'succession.plans.tour_assessment_description' }),
        targetSelector: '[data-tour="assessment-tab"]',
        position: 'bottom'
      });
    }

    if (ceAssessmentEnabledForTour) {
      steps.push({
        id: 'ce-tab',
        title: intl.formatMessage({ id: 'succession.plans.tour_ce_title' }),
        description: intl.formatMessage({ id: 'succession.plans.tour_ce_description' }),
        targetSelector: '[data-tour="ce-tab"]',
        position: 'bottom'
      });
    }

    return steps;
  }, [isAssessmentPlanForTour, ceAssessmentEnabledForTour, intl]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-succession-primary mx-auto mb-4"></div>
        <p className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.plans.loading_completed_plan' })}</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center text-succession-error">
        <p>{intl.formatMessage({ id: 'succession.plans.error_loading_plan_results' })}</p>
        <p className="text-sm">{error.message}</p>
      </div>
    </div>
  );

  const progress = data?.userPlanProgress;
  const currentUser = data?.getCurrentUser;
  
  // Use progress ID for localStorage key so tour shows for each new plan completion
  const completionTourKey = `plan_completion_tour_shown_${progress?.id || planTemplateId}`;
  
  const userName = currentUser?.allowed_name || currentUser?.name || 'User';
  const companyName = currentUser?.company?.name || 'Practice';
  const planName = progress?.planTemplate?.name || 'Succession Plan';
  
  // Reuse values computed before early returns for tour
  const ceAssessmentEnabled = ceAssessmentEnabledForTour;
  const isAssessmentPlan = isAssessmentPlanForTour;

  const generatedDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Stored scores from backend (source of truth for 12-question diagnostic)
  const storedScores = {
    vmiReadinessScore: currentUser?.vmi_readiness_score,
    diagOwnerAnswers: currentUser?.diag_owner_answers,
    diagBusinessAnswers: currentUser?.diag_business_answers
  };

  // Calculate document statistics
  const documentStructure = DocumentGeneratorService.getDocumentStructure(progress, userName, companyName);
  
  const getAssessmentStats = () => {
    const assessmentQuestions = progress?.userPlanResponses?.filter(r => {
      const question = findQuestion(r.planQuestionId);
      return question?.purpose === 'assessment' || question?.purpose === 'both';
    }) || [];
    
    // Count unique categories
    const categories = new Set();
    assessmentQuestions.forEach(r => {
      const question = findQuestion(r.planQuestionId);
      if (question?.category) categories.add(question.category);
    });
    
    return {
      categoryCount: categories.size || 5,
      gapCount: documentStructure?.vmiAssessment?.gapAlerts?.length || 0,
      recommendationCount: documentStructure?.vmiAssessment?.gapAlerts?.length || 0
    };
  };

  const getPlanStats = () => {
    const sections = new Set();
    progress?.planTemplate?.planModules?.forEach(mod => {
      mod.planSections?.forEach(sec => {
        sec.planQuestions?.forEach(q => {
          if (q.outputSection) sections.add(q.outputSection);
        });
      });
    });
    
    const wordCount = progress?.userPlanResponses?.reduce((acc, r) => {
      const value = r.answer?.value || '';
      const text = typeof value === 'string' ? value : JSON.stringify(value);
      return acc + text.split(/\s+/).length;
    }, 0) || 0;
    
    return {
      sectionCount: sections.size || 11,
      wordCount: wordCount,
      pageEstimate: Math.ceil(wordCount / 400) || 12
    };
  };

  const findQuestion = (questionId) => {
    for (const mod of progress?.planTemplate?.planModules || []) {
      for (const sec of mod.planSections || []) {
        const q = sec.planQuestions?.find(q => q.id === questionId);
        if (q) return q;
      }
    }
    return null;
  };

  const assessmentStats = getAssessmentStats();
  const planStats = getPlanStats();

  // Generate markdown content for preview
  const getPreviewContent = () => {
    if (!progress) return '';
    if (activeTab === 'assessment' && isAssessmentPlan) {
      return DocumentGeneratorService.generateAssessmentReportMarkdown(progress, userName, storedScores);
    }
    // Use plan-type-specific generator for plan document
    return generatePlanDocumentByType(progress, userName, companyName);
  };

  // Handle downloads
  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    
    if (type === 'assessment-md' && isAssessmentPlan) {
      const content = DocumentGeneratorService.generateAssessmentReportMarkdown(progress, userName, storedScores);
      PDFExportService.exportMarkdownToFile(content, `${userName.replace(/\s/g, '_')}_Assessment.md`);
    } else if (type === 'plan-md') {
      // Use plan-type-specific generator for plan document
      const content = generatePlanDocumentByType(progress, userName, companyName);
      PDFExportService.exportMarkdownToFile(content, `${userName.replace(/\s/g, '_')}_Plan.md`);
    } else if (type === 'pdf' && documentRef.current) {
      await PDFExportService.exportHtmlToPdf(documentRef.current, `${userName.replace(/\s/g, '_')}_${planName.replace(/\s/g, '_')}.pdf`);
    }
  };

  // Check if vault feature is available (either company or user level)
  const hasVaultFeature = currentUser?.company?.features?.includes('vault') || 
                          currentUser?.features?.includes('vault');

  // Handle saving to vault
  const handleSaveToVault = async (documentType) => {
    setShowVaultMenu(false);
    setVaultSaveStatus(prev => ({ ...prev, [documentType]: 'saving' }));
    
    try {
      let content;
      let filename;
      
      if (documentType === 'assessment' && isAssessmentPlan) {
        content = DocumentGeneratorService.generateAssessmentReportMarkdown(progress, userName, storedScores);
        filename = `${userName.replace(/\s/g, '_')}_Assessment_Report.md`;
      } else {
        content = generatePlanDocumentByType(progress, userName, companyName);
        filename = `${userName.replace(/\s/g, '_')}_${planName.replace(/\s/g, '_')}.md`;
      }
      
      await savePlanToVault({
        variables: {
          planProgressId: progress.id,
          documentType: documentType,
          content: content,
          filename: filename
        }
      });
      
      setVaultSaveStatus(prev => ({ ...prev, [documentType]: 'saved' }));
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setVaultSaveStatus(prev => ({ ...prev, [documentType]: 'idle' }));
      }, 3000);
    } catch (err) {
      console.error('Error saving to vault:', err);
      setVaultSaveStatus(prev => ({ ...prev, [documentType]: 'error' }));
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setVaultSaveStatus(prev => ({ ...prev, [documentType]: 'idle' }));
      }, 3000);
    }
  };

  // Handle saving both documents to vault
  const handleSaveAllToVault = async () => {
    setShowVaultMenu(false);
    
    // Save plan document
    await handleSaveToVault('plan');
    
    // Save assessment if applicable
    if (isAssessmentPlan) {
      await handleSaveToVault('assessment');
    }
  };

  // Get vault button status label
  const getVaultButtonLabel = (type) => {
    const status = vaultSaveStatus[type];
    if (status === 'saving') return intl.formatMessage({ id: 'succession.plans.vault_saving' });
    if (status === 'saved') return intl.formatMessage({ id: 'succession.plans.vault_saved' });
    if (status === 'error') return intl.formatMessage({ id: 'succession.plans.vault_error' });
    return type === 'assessment' ? intl.formatMessage({ id: 'succession.plans.vault_assessment_report' }) : intl.formatMessage({ id: 'succession.plans.vault_plan_document' });
  };

  // Calculate total pages for pagination
  const totalPages = planStats.pageEstimate || 24;

  return (
    <div ref={scrollContainerRef} className="min-h-full bg-succession-bg-secondary overflow-y-auto">
      {/* Header above tabs */}
      <div className="bg-succession-bg-primary border-b border-succession-border">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <h1 className="text-lg font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.your_plan_header' }, { planName })}</h1>
          <p className="text-sm text-succession-text-secondary">
            {isAssessmentPlan
              ? intl.formatMessage({ id: 'succession.plans.congrats_with_assessment' }, { planName: planName.toLowerCase() })
              : intl.formatMessage({ id: 'succession.plans.congrats_plan_only' }, { planName: planName.toLowerCase() })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-succession-bg-primary border-b border-succession-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('plan')}
              data-tour="plan-tab"
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'plan'
                  ? 'border-succession-primary text-succession-primary bg-succession-bg-secondary'
                  : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-secondary'
              }`}
            >
              <FileTextIcon className="w-4 h-4" />
              {intl.formatMessage({ id: 'succession.plans.tab_your_plan' })}
            </button>
            {isAssessmentPlan && (
              <button
                onClick={() => setActiveTab('assessment')}
                data-tour="assessment-tab"
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'assessment'
                    ? 'border-succession-primary text-succession-primary bg-succession-bg-secondary'
                    : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-secondary'
                }`}
              >
                <SparklesIcon className="w-4 h-4" />
                {intl.formatMessage({ id: 'succession.plans.tab_readiness_assessment' })}
              </button>
            )}
            {ceAssessmentEnabled && (
              <button
                onClick={() => setActiveTab('ce-assessment')}
                data-tour="ce-tab"
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'ce-assessment'
                    ? 'border-succession-primary text-succession-primary bg-succession-bg-secondary'
                    : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-secondary'
                }`}
              >
                <GraduationCapIcon className="w-4 h-4" />
                {intl.formatMessage({ id: 'succession.plans.tab_earn_ce_credit' })}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'ce-assessment' && ceAssessmentEnabled ? (
        <CeAssessmentContainer
          planTemplateId={planTemplateId}
          userPlanId={progress?.id}
          userName={userName}
          onComplete={(result) => {
            // After completing CE assessment, switch back to plan tab
            if (result?.passed) {
              setActiveTab('plan');
            }
          }}
          onSkip={() => setActiveTab('plan')}
        />
      ) : activeTab === 'assessment' && isAssessmentPlan ? (
        <SuccessionReadinessAssessment
          progress={progress}
          userName={userName}
          storedScores={storedScores}
          onDownload={() => handleDownload('assessment-md')}
          onShare={onShare}
        />
      ) : (
      <div className="max-w-6xl mx-auto p-6 md:p-8 space-y-8">
        {/* Hero Section */}
        <div className="text-center space-y-4 py-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-succession-primary/10 mb-4">
            <PartyPopperIcon className="h-10 w-10 text-succession-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-succession-text-primary">
            {intl.formatMessage({ id: 'succession.plans.plan_ready_heading' }, { planName })}
          </h1>
          <p className="text-lg text-succession-text-secondary max-w-2xl mx-auto">
            {intl.formatMessage({ id: 'succession.plans.plan_ready_description' }, { planName: planName.toLowerCase() })}
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-succession-text-muted">
            <CalendarIcon className="w-4 h-4" />
            <span>{intl.formatMessage({ id: 'succession.plans.generated_on' }, { date: generatedDate })}</span>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {/* Download PDF Button */}
          <div className="relative" ref={downloadMenuRef}>
            <Button
              size="lg"
              className="bg-succession-primary hover:bg-succession-primary/90 text-white"
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
            >
              <DownloadIcon className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.download_pdf' })}
            </Button>

            {showDownloadMenu && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg z-50">
                <div className="py-2">
                  {/* Assessment Report - Only for assessment-based plans */}
                  {isAssessmentPlan && (
                    <button
                      onClick={() => handleDownload('assessment-md')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2"
                    >
                      <BarChart3Icon className="w-4 h-4 text-succession-primary" />
                      {intl.formatMessage({ id: 'succession.plans.download_assessment_md' })}
                    </button>
                  )}
                  <button
                    onClick={() => handleDownload('plan-md')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2"
                  >
                    <FileTextIcon className="w-4 h-4 text-succession-secondary" />
                    {intl.formatMessage({ id: 'succession.plans.download_plan_md' })}
                  </button>
                  <hr className="my-2 border-succession-border" />
                  <button
                    onClick={() => handleDownload('pdf')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2"
                  >
                    <DownloadIcon className="w-4 h-4 text-succession-primary" />
                    {intl.formatMessage({ id: 'succession.plans.download_current_view_pdf' })}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Save to Vault Dropdown */}
          {hasVaultFeature && (
            <div className="relative" ref={vaultMenuRef}>
              <Button
                size="lg"
                variant="outline"
                className="border-succession-secondary text-succession-secondary hover:bg-succession-secondary/10"
                onClick={() => setShowVaultMenu(!showVaultMenu)}
              >
                <ArchiveIcon className="w-5 h-5 mr-2" />
                {intl.formatMessage({ id: 'succession.plans.save_to_vault' })}
                <ChevronDownIcon className="w-4 h-4 ml-2" />
              </Button>
              
              {showVaultMenu && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-succession-bg-primary border border-succession-border rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    {/* Assessment Report - Only for assessment-based plans */}
                    {isAssessmentPlan && (
                      <button
                        onClick={() => handleSaveToVault('assessment')}
                        disabled={vaultSaveStatus.assessment === 'saving'}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2 disabled:opacity-50"
                      >
                        {vaultSaveStatus.assessment === 'saving' ? (
                          <Loader2Icon className="w-4 h-4 text-succession-primary animate-spin" />
                        ) : vaultSaveStatus.assessment === 'saved' ? (
                          <CheckCircleIcon className="w-4 h-4 text-succession-success" />
                        ) : (
                          <BarChart3Icon className="w-4 h-4 text-succession-primary" />
                        )}
                        {getVaultButtonLabel('assessment')}
                      </button>
                    )}
                    <button
                      onClick={() => handleSaveToVault('plan')}
                      disabled={vaultSaveStatus.plan === 'saving'}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2 disabled:opacity-50"
                    >
                      {vaultSaveStatus.plan === 'saving' ? (
                        <Loader2Icon className="w-4 h-4 text-succession-secondary animate-spin" />
                      ) : vaultSaveStatus.plan === 'saved' ? (
                        <CheckCircleIcon className="w-4 h-4 text-succession-success" />
                      ) : (
                        <FileTextIcon className="w-4 h-4 text-succession-secondary" />
                      )}
                      {getVaultButtonLabel('plan')}
                    </button>
                    <hr className="my-2 border-succession-border" />
                    <button
                      onClick={handleSaveAllToVault}
                      disabled={vaultSaveStatus.plan === 'saving' || vaultSaveStatus.assessment === 'saving'}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-succession-bg-secondary flex items-center gap-2 disabled:opacity-50 font-medium"
                    >
                      <ArchiveIcon className="w-4 h-4 text-succession-secondary" />
                      {intl.formatMessage({ id: 'succession.plans.save_all_to_vault' })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {onEmail && (
            <Button size="lg" variant="outline" onClick={onEmail}>
              <MailIcon className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.button_email' })}
            </Button>
          )}
          {onShare && (
            <Button size="lg" variant="outline" onClick={onShare}>
              <Share2Icon className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.button_share' })}
            </Button>
          )}
          {onEditPlan && (
            <Button size="lg" variant="outline" onClick={onEditPlan}>
              <EditIcon className="w-5 h-5 mr-2" />
              {intl.formatMessage({ id: 'succession.plans.button_edit_plan' })}
            </Button>
          )}
        </div>

        {/* Stats Cards - 3 cards for sections, pages, words */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sections Card */}
          <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <LayoutListIcon className="w-8 h-8 text-succession-primary" />
            </div>
            <div className="text-3xl font-bold text-succession-text-primary">
              {planStats.sectionCount}
            </div>
            <div className="text-base font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.stat_sections' })}
            </div>
            <div className="text-sm text-succession-text-muted mt-1">
              {intl.formatMessage({ id: 'succession.plans.stat_sections_subtitle' })}
            </div>
          </div>

          {/* Pages Card */}
          <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <BookOpenIcon className="w-8 h-8 text-succession-secondary" />
            </div>
            <div className="text-3xl font-bold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.stat_pages_count' }, { count: totalPages })}
            </div>
            <div className="text-sm text-succession-text-muted mt-1">
              {intl.formatMessage({ id: 'succession.plans.stat_pages_subtitle' })}
            </div>
          </div>

          {/* Words Card */}
          <div className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 text-center">
            <div className="flex justify-center mb-4">
              <FileIcon className="w-8 h-8 text-succession-primary" />
            </div>
            <div className="text-3xl font-bold text-succession-text-primary">
              {planStats.wordCount.toLocaleString()}
            </div>
            <div className="text-base font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.plans.stat_words' })}
            </div>
            <div className="text-sm text-succession-text-muted mt-1">
              {intl.formatMessage({ id: 'succession.plans.stat_words_subtitle' })}
            </div>
          </div>
        </div>

        {/* Document Viewer */}
        <div className="bg-succession-bg-primary border border-succession-border rounded-lg overflow-hidden">
          {/* Viewer Header with Pagination and Zoom */}
          <div className="flex items-center justify-between p-3 border-b border-succession-border bg-succession-bg-secondary">
            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </Button>
              <span className="text-sm text-succession-text-primary min-w-[100px] text-center">
                {intl.formatMessage({ id: 'succession.plans.page_of_total' }, { current: currentPage, total: totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.max(50, zoom - 10))}
                disabled={zoom <= 50}
                className="h-8 w-8 p-0"
              >
                <ZoomOutIcon className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium px-2 text-succession-text-primary min-w-[50px] text-center">{zoom}%</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.min(150, zoom + 10))}
                disabled={zoom >= 150}
                className="h-8 w-8 p-0"
              >
                <ZoomInIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Document Preview - Scrollable */}
          <div className="bg-gray-100 dark:bg-gray-900 p-4 md:p-8 max-h-[600px] overflow-auto">
            <div
              ref={documentRef}
              className="mx-auto"
              style={{
                maxWidth: '850px',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center'
              }}
            >
              <PlanPdfCurrentView content={getPreviewContent()} />
            </div>
          </div>
        </div>

        {/* CE Assessment Card - Prominent placement when available */}
        {ceAssessmentEnabled && (
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.earn_ce_credit_heading' })}</h2>
            <CeAssessmentIntroCard
              creditHours={1}
              questionCount={20}
              estimatedMinutes={20}
              passingThreshold={70}
              courseName={planName}
              onStart={() => setActiveTab('ce-assessment')}
              onSkip={() => {}}
              className="max-w-4xl"
            />
          </section>
        )}

        {/* Next Steps */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.next_steps_heading' })}</h2>

          <div className={`grid grid-cols-1 ${isAssessmentPlan ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
            <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <UserIcon className="w-8 h-8 text-succession-primary mb-4" />
              <h3 className="font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.plans.next_step_advisor_title' })}</h3>
              <p className="text-sm text-succession-text-secondary mb-4">
                {intl.formatMessage({ id: 'succession.plans.next_step_advisor_description' })}
              </p>
              <Button variant="outline" size="sm" className="w-full">
                {intl.formatMessage({ id: 'succession.plans.next_step_advisor_button' })}
              </Button>
            </div>

            <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <CalendarIcon className="w-8 h-8 text-succession-secondary mb-4" />
              <h3 className="font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.plans.next_step_update_title' })}</h3>
              <p className="text-sm text-succession-text-secondary mb-4">
                {intl.formatMessage({ id: 'succession.plans.next_step_update_description' })}
              </p>
              <Button variant="outline" size="sm" className="w-full">
                {intl.formatMessage({ id: 'succession.plans.next_step_update_button' })}
              </Button>
            </div>

            {/* Value Acceleration - Only for assessment-based plans */}
            {isAssessmentPlan && (
              <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                <TrendingUpIcon className="w-8 h-8 text-succession-success mb-4" />
                <h3 className="font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.plans.next_step_dashboard_title' })}</h3>
                <p className="text-sm text-succession-text-secondary mb-4">
                  {intl.formatMessage({ id: 'succession.plans.next_step_dashboard_description' })}
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => window.location.href = '/succession/value_acceleration?from=plan_builder'}
                >
                  {intl.formatMessage({ id: 'succession.plans.next_step_dashboard_button' })}
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
      )}

      {/* Guided Tour for Plan Completion */}
      <GuidedTour
        isOpen={showCompletionTour}
        steps={completionTourSteps}
        onClose={() => {
          try { localStorage.setItem(completionTourKey, 'true'); } catch (_) {}
          setShowCompletionTour(false);
        }}
        onComplete={() => {
          try { localStorage.setItem(completionTourKey, 'true'); } catch (_) {}
          setShowCompletionTour(false);
        }}
        onReplayRequest={() => setShowCompletionTour(true)}
      />
    </div>
  );
};

export default PlanCompletionCelebration;
